// Distribution Mappers

import {
  DistributionRecord,
  DistributionMarketer,
  DistributionProduct,
  AvailableSerial,
  CreateDeliveryForm,
  UpdateDeliveryForm,
  DistributionFilters,
  DistributionStatus,
  SerialStatus
} from '../models/distribution.model';

import {
  DistributionRecordDTO,
  DistributionMarketerDTO,
  DistributionProductDTO,
  AvailableSerialDTO,
  CreateDeliveryRequestDTO,
  UpdateDeliveryRequestDTO,
  DistributionFiltersDTO
} from '../models/distribution.dto';

import {PAGINATION_CONFIG} from '../../../constants/appConfig';

// DTO to Model Mappers
export const mapDistributionRecordDTOToModel = (dto: DistributionRecordDTO): DistributionRecord => {
  // Safe access to nested properties
  const product = dto.product || {};
  const marketer = dto.marketer || {};
  const deliveredBy = dto.deliveredBy || {};

  const rawPrice = product.price;
  const parsedPrice = rawPrice ? parseFloat(rawPrice) || 0 : 0;

  // Add fallback values for missing properties
  const result = {
    id: dto.id?.toString() || '0',
    marketerId: dto.marketer_id?.toString() || '0',
    marketerName: marketer.full_name || 'غير محدد',
    marketerUsername: marketer.username || 'غير محدد',
    productId: dto.product_id?.toString() || '0',
    productName: product.name || 'غير محدد',
    productBarcode: product.barcode || '',
    productImages: product.images || [], // Add product images
    quantity: dto.quantity || (dto.serials && dto.serials.length > 0 ? dto.serials.length : 0),
    hasSerial: product.has_serial || false,
    serials: dto.serials || [],
    deliveredAt: dto.delivered_at || new Date().toISOString(),
    deliveredBy: dto.delivered_by?.toString() || dto.deliveredBy?.id?.toString() || '0',
    deliveredByName: deliveredBy.full_name || 'غير محدد',
    status: 'completed' as DistributionStatus, // Default to completed since these are delivery records
    notes: dto.transaction_type || '',
    location: marketer.latitude && marketer.longitude ? `${marketer.latitude}, ${marketer.longitude}` : 'غير محدد',
    cost: parsedPrice
  };

  return result;
};

export const mapDistributionMarketerDTOToModel = (dto: DistributionMarketerDTO): DistributionMarketer => ({
  id: dto.id.toString(),
  username: dto.username,
  fullName: dto.full_name,
  email: dto.email,
  phone: dto.phone || dto.phone_number, // Try both fields
  area: dto.area,
  isActive: dto.is_active ?? false,
  totalDeliveries: dto.total_deliveries ?? 0,
  lastDeliveryDate: dto.last_delivery_date
});

export const mapDistributionProductDTOToModel = (dto: DistributionProductDTO): DistributionProduct => {
  const result = {
    id: dto.id.toString(),
    name: dto.name,
    barcode: dto.barcode,
    hasSerial: dto.has_serial,
    availableQuantity: dto.available_quantity,
    price: typeof dto.price === 'string' ? parseFloat(dto.price) || 0 : dto.price,
    brand: dto.brand,
    type: dto.type,
    warehouseLocation: dto.warehouse_location,
    category: dto.category,
    description: dto.description,
    images: dto.images
  };

  return result;
};

export const mapAvailableSerialDTOToModel = (dto: AvailableSerialDTO): AvailableSerial => ({
  id: dto.id.toString(),
  productId: dto.product_id.toString(),
  serialNumber: dto.serial_number,
  locationId: dto.location_id?.toString(),
  locationName: dto.location_name,
  status: dto.status as SerialStatus,
  createdAt: dto.created_at
});

// Model to DTO Mappers
export const mapCreateDeliveryFormToDTO = (form: CreateDeliveryForm, hasSerial: boolean): CreateDeliveryRequestDTO => {
  if (!form.marketerId || !form.productId) {
    throw new Error('marketerId and productId are required');
  }

  // Validate deliveredBy is provided
  if (!form.deliveredBy) {
    throw new Error('deliveredBy is required. Please ensure you are authenticated.');
  }

  const baseRequest: Partial<CreateDeliveryRequestDTO> = {
    marketer_id: parseInt(form.marketerId),
    product_id: parseInt(form.productId),
    delivered_by: parseInt(form.deliveredBy) // Always include delivered_by when available
  };

  // Only include scheduled_date if it exists
  if (form.scheduledDate) {
    baseRequest.scheduled_date = form.scheduledDate;
  }

  // For products with serials, use serials array and omit quantity
  if (hasSerial && form.serials && form.serials.length > 0) {
    return {
      ...baseRequest,
      serials: form.serials,
      marketer_id: baseRequest.marketer_id ?? 0,
      product_id: baseRequest.product_id ?? 0
    };
  }

  // For products without serials, use quantity and omit serials
  return {
    ...baseRequest,
    quantity: form.quantity,
    marketer_id: baseRequest.marketer_id ?? 0,
    product_id: baseRequest.product_id ?? 0
  };
};

export const mapUpdateDeliveryFormToDTO = (form: UpdateDeliveryForm): UpdateDeliveryRequestDTO => ({
  quantity: form.quantity,
  serials: form.serials,
  status: form.status,
  delivered_at: form.deliveredAt
});

export const mapDistributionFiltersToDTO = (
  filters: DistributionFilters,
  page = 1,
  limit = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc'
): DistributionFiltersDTO => ({
  marketer_id: filters.marketerId ? parseInt(filters.marketerId) : undefined,
  product_id: filters.productId ? parseInt(filters.productId) : undefined,
  status: filters.status,
  delivered_by: filters.deliveredBy ? parseInt(filters.deliveredBy) : undefined,
  date_from: filters.dateFrom,
  date_to: filters.dateTo,
  has_serial: filters.hasSerial,
  location: filters.location,
  min_quantity: filters.minQuantity,
  max_quantity: filters.maxQuantity,
  page,
  limit,
  sort_by: sortBy,
  sort_order: sortOrder
});

// Array Mappers
export const mapDistributionRecordsDTOToModel = (dtos: DistributionRecordDTO[]): DistributionRecord[] =>
  dtos.map(mapDistributionRecordDTOToModel);

export const mapDistributionMarketersDTOToModel = (dtos: DistributionMarketerDTO[]): DistributionMarketer[] =>
  dtos.map(mapDistributionMarketerDTOToModel);

export const mapDistributionProductsDTOToModel = (dtos: DistributionProductDTO[]): DistributionProduct[] =>
  dtos.map(mapDistributionProductDTOToModel);

export const mapAvailableSerialsDTOToModel = (dtos: AvailableSerialDTO[]): AvailableSerial[] =>
  dtos.map(mapAvailableSerialDTOToModel);
